import apiService from '@/services/apiService'
import authService from '@/services/authService'

class HeartbeatService {
  constructor() {
    this.interval = null
    this.isWindowFocused = true
    this.lastActivity = Date.now()
    this.sessionStart = Date.now()
    this.pageViews = []
    this.currentPage = window.location.pathname
    
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Ablak fókusz követése
    window.addEventListener('focus', () => {
      this.isWindowFocused = true
      this.updateActivity()
    })

    window.addEventListener('blur', () => {
      this.isWindowFocused = false
    })

    // Felhasználói aktivitás követése
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    activityEvents.forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), { passive: true })
    })

    // Oldal változás követése
    window.addEventListener('beforeunload', () => {
      this.trackPageView()
    })

    // Route változás követése (Vue Router esetén)
    if (window.router) {
      window.router.afterEach((to) => {
        this.trackPageView()
        this.currentPage = to.path
      })
    }
  }

  updateActivity() {
    this.lastActivity = Date.now()
  }

  trackPageView() {
    if (this.currentPage) {
      const pageView = {
        path: this.currentPage,
        duration: Date.now() - this.lastActivity,
        timestamp: new Date().toISOString()
      }
      this.pageViews.push(pageView)
      
      // Csak az utolsó 10 oldal megtekintését tartjuk meg
      if (this.pageViews.length > 10) {
        this.pageViews.shift()
      }
    }
  }

  getSystemInfo() {
    return {
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      connection_type: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      battery_level: navigator.getBattery ? 'available' : 'unavailable'
    }
  }

  async sendHeartbeat() {
    if (!authService.isAuthenticated()) {
      this.stop()
      return
    }

    const now = Date.now()
    const isIdle = (now - this.lastActivity) > 300000 // 5 perc inaktivitás
    
    const heartbeatData = {
      // Alapadatok
      timestamp: new Date().toISOString(),
      session_id: this.getSessionId(),
      
      // Aktivitás adatok
      is_active: this.isWindowFocused && !isIdle,
      is_window_focused: this.isWindowFocused,
      is_idle: isIdle,
      last_activity: new Date(this.lastActivity).toISOString(),
      
      // Oldal adatok
      current_page: this.currentPage,
      page_title: document.title,
      referrer: document.referrer,
      
      // Időadatok
      session_duration: Math.floor((now - this.sessionStart) / 1000), // másodpercben
      time_on_current_page: Math.floor((now - this.lastActivity) / 1000),
      
      // Rendszeradatok
      system_info: this.getSystemInfo(),
      
      // Teljesítményadatok
      performance: this.getPerformanceData(),
      
      // Oldal megtekintések (utolsó néhány)
      recent_page_views: this.pageViews.slice(-5),
      
      // Hálózat állapot
      online_status: navigator.onLine,
      
      // Memória használat (ha elérhető)
      memory_usage: this.getMemoryUsage()
    }

    try {
      const response = await apiService.post('/heartbeat', heartbeatData)
      
      if (!response.success) {
      }
      
      return response
    } catch (error) {
      
      // Ha 401-es hibát kapunk, a felhasználó nincs bejelentkezve
      if (error.response?.status === 401) {
        this.stop()
        authService.logout()
        window.location.href = '/login'
      }
    }
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('session_id')
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      sessionStorage.setItem('session_id', sessionId)
    }
    return sessionId
  }

  getPerformanceData() {
    if (window.performance && window.performance.memory) {
      return {
        used_memory: Math.round(window.performance.memory.usedJSHeapSize / 1024 / 1024), // MB
        total_memory: Math.round(window.performance.memory.totalJSHeapSize / 1024 / 1024), // MB
        memory_limit: Math.round(window.performance.memory.jsHeapSizeLimit / 1024 / 1024), // MB
        load_time: window.performance.timing ? window.performance.timing.loadEventEnd - window.performance.timing.navigationStart : null
      }
    }
    return null
  }

  getMemoryUsage() {
    if ('memory' in performance) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024)
      }
    }
    return null
  }

  start() {
    if (this.interval) {
      this.stop()
    }

    // Azonnali első heartbeat
    this.sendHeartbeat()
    
    // Fél percenkénti heartbeat
    this.interval = setInterval(() => {
      this.sendHeartbeat()
    }, 30000) // 30 másodperc

  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  // Manuális heartbeat küldése (pl. fontos műveletek előtt)
  async sendImmediateHeartbeat() {
    return await this.sendHeartbeat()
  }
}

export default new HeartbeatService()